// ─── components/Notifications/NeedsYouTracker.jsx ────────────────────────────
// Follows you everywhere, Home included — the single place the "Needs you"
// queue lives now (Home no longer has its own inline copy of this).
//
// There's no manual "check it off" — this reads the exact same
// GET /api/dashboard/action-queue Home does, so the moment you actually
// approve an order or read a message elsewhere, the next refetch here just
// no longer includes it. It disappears entirely on its own once the list is
// empty; there's nothing to dismiss because there's nothing left to say.
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PiCaretDown, PiChatText, PiCreditCard, PiFileText, PiX } from 'react-icons/pi';
import { BadgeCount } from '../UI/Badge';

const ICON_BY_TYPE = { order: PiFileText, contract: PiFileText, message: PiChatText, invoice: PiCreditCard };
const REFRESH_MS = 60 * 1000;

// Below `sm` this floats over roughly three-quarters of the screen width, so
// starting it expanded there would block whatever page content sits behind
// it (a form field, a button) rather than just sitting in an unused corner
// the way it does on desktop. Collapsed-by-default on mobile keeps it a
// small tappable pill instead — nothing is hidden, just not shown unasked.
const startsCollapsed = () => typeof window !== 'undefined' && window.innerWidth < 640;

export default function NeedsYouTracker({ user }) {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [collapsed, setCollapsed] = useState(startsCollapsed);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const load = () => {
      fetch('/api/dashboard/action-queue', { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (!cancelled && d) setItems(d.items || []); })
        .catch(() => {});
    };
    load();
    const interval = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, location.pathname]);

  // The dashboards list the same queue inline (Needs you on Overview, the
  // Work tab's status line), so the floating copy would only repeat it there.
  const onDashboard = /^\/(seller|hiring)\/dashboard/.test(location.pathname);

  if (!user || dismissed || onDashboard || items.length === 0) return null;

  return (
    <div className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-[max(1rem,env(safe-area-inset-left))] z-[110] w-[calc(100vw-2rem)] max-w-[280px] font-academy sm:bottom-6 sm:left-6">
      <div className="overflow-hidden rounded-2xl border border-press-border bg-press-card shadow-xl">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-expanded={!collapsed}
          className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:opacity-80"
        >
          <span className="flex items-center gap-2">
            <span aria-live="polite">
              <BadgeCount value={items.length} label={`${items.length} still need you`} />
            </span>
            <span className="text-[13px] font-bold text-press-ink">Still needs you</span>
          </span>
          <span className="flex items-center gap-1">
            {/* A real <button> here would nest inside the header's own button
                (invalid HTML — the header toggles collapse on click), so this
                stays a span with a manual role. Both Enter and Space need to
                fire it, same as a native button would. */}
            <span
              role="button"
              tabIndex={0}
              aria-label="Dismiss"
              onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                  e.preventDefault();
                  e.stopPropagation();
                  setDismissed(true);
                }
              }}
              className="flex h-6 w-6 items-center justify-center rounded-full text-press-muted transition hover:bg-press-paper-2 hover:text-press-ink"
            >
              <PiX size={13} aria-hidden="true" />
            </span>
            <PiCaretDown size={14} className={`text-press-muted transition-transform ${collapsed ? '' : 'rotate-180'}`} aria-hidden="true" />
          </span>
        </button>

        {!collapsed && (
          <div className="max-h-[280px] overflow-y-auto border-t border-press-border p-2">
            {items.map((item, i) => {
              const Icon = ICON_BY_TYPE[item.type] || PiFileText;
              return (
                <Link
                  key={`${item.type}-${i}`}
                  to={item.link}
                  className="flex items-start gap-2.5 rounded-xl p-2 transition hover:bg-press-paper-2"
                >
                  <span className={`mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-[8px] ${item.urgent ? 'bg-press-ink text-press-paper' : 'border border-press-border bg-press-paper-2 text-press-brass-ink'}`}>
                    <Icon size={13} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12px] font-semibold text-press-ink">{item.title}</span>
                    <span className="block truncate text-[12px] text-press-muted">{item.subtitle}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
